<template>
  <PopoverRoot v-model:open="isOpen" :modal="true">
    <PopoverTrigger
      class="inline-flex px-4 py-2 border-border-secondary border rounded-lg bg-surface text-text text-base leading-loose w-full hover:bg-mauve3 focus:shadow-[0_0_0_2px] focus:shadow-black data-[placeholder]:text-green9 outline-none"
      :aria-label="$t('common.countrySelector.selectCountry')"
      :disabled="readonly"
    >
      <div
        class="flex flex-row items-center gap-1.5 [@media_(min-width:_450px)]:min-w-[88px]"
      >
        <Icon
          v-if="currentCountryData"
          :icon="currentCountryData.flagIcon"
          class="inline"
        />

        <span v-if="currentCountryData">
          {{ "+" + currentCountryData.dialCode }}
        </span>

        <Icon
          icon="radix-icons:chevron-down"
          class="size-4 ml-auto [@media_(max-width:_450px)]:hidden"
        />
      </div>
    </PopoverTrigger>

    <PopoverPortal :to="portalRootElementSelector">
      <PopoverContent
        class="min-w-[160px] bg-brand-bg-main border-2 border-brand-bgdivider-20 rounded shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),_0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)] will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade z-[9999999999]"
        :side-offset="5"
      >
        <ListboxRoot
          :id="id"
          v-model="countryCode"
          :name="name"
          :multiple="false"
          highlight-on-hover
          selection-behavior="replace"
          class="flex flex-col gap-2"
          :disabled="readonly"
        >
          <ListboxContent class="p-2 min-w-[260px] h-72 overflow-auto">
            <ListboxVirtualizer
              v-slot="{ option: country }"
              :options="formattedCountries"
              :text-content="(c) => c.label"
              :estimate-size="tileHeight"
            >
              <ListboxItem
                :key="country.value"
                :style="{ height: tileHeightWithUnit }"
                class="text-[13px] leading-none text-text data-[state=checked]:text-text-on-brand data-[state=checked]:data-[highlighted]:text-text rounded-[3px] flex items-center pr-[18px] pl-[18px] relative select-none data-[disabled]:pointer-events-none data-[highlighted]:outline-none data-[state=checked]:bg-brand-primary data-[highlighted]:!bg-brand-surface-hover justify-between w-full data-[disabled]:opacity-50"
                :value="country.value"
                :disabled="country.isDisabled"
                @select="() => (isOpen = false)"
              >
                <span>
                  <Icon :icon="country.flagIcon" class="inline mr-2" />
                  {{ country.label }}
                  <span v-if="country.isDisabled">
                    {{ $t("ui.phoneNumber.unavailableToSendSms") }}
                  </span>
                </span>
                <ListboxItemIndicator>
                  <Icon icon="radix-icons:check" class="text-2xl" />
                </ListboxItemIndicator>
              </ListboxItem>
            </ListboxVirtualizer>
          </ListboxContent>
        </ListboxRoot>
      </PopoverContent>
    </PopoverPortal>
  </PopoverRoot>
</template>

<script setup lang="ts">
import {
  CountryData,
  type CountryCode,
  type CountryInformation,
} from "@/constants/country";
import { portalRootElementSelector } from "@/constants/ui";
import { Icon } from "@iconify/vue";
import {
  ListboxContent,
  ListboxItem,
  ListboxItemIndicator,
  ListboxRoot,
  ListboxVirtualizer,
  PopoverContent,
  PopoverPortal,
  PopoverRoot,
  PopoverTrigger,
} from "reka-ui";
import { computed, ref } from "vue";

defineOptions({
  inheritAttrs: false,
});

const countryCode = defineModel<CountryCode | undefined>({
  required: true,
});

defineProps<{
  readonly?: boolean;
  id?: string;
  name?: string;
}>();

const isOpen = ref(false);

const tileHeight = 35;
const tileHeightWithUnit = `${tileHeight}px`;

const formatCountryName = new Intl.DisplayNames(window.navigator.language, {
  type: "region",
});
function formatCountry({ code, dial_code, isDisabled }: CountryInformation) {
  return {
    value: code,
    flagIcon: `flag:${code.toLocaleLowerCase()}-4x3`,
    label: `${formatCountryName.of(code)} (${dial_code})`,
    dialCode: dial_code,
    isDisabled: !!isDisabled,
  };
}

const formattedCountries = computed(() =>
  Object.values(CountryData.countries).map(formatCountry),
);

const currentCountryData = computed(() =>
  countryCode.value
    ? formatCountry(CountryData.countries[countryCode.value])
    : undefined,
);
</script>
